import { ChangeDetectorRef, Component, NgZone, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';

import { TranslateService } from '@ngx-translate/core';
import * as moment from 'moment';
import { PromotionLeaderboardService } from '../../core/service/promotion-leaderboard.service';
import { CommonService } from '../../core/service/common.service';
import { DTO } from '../../../app/core/models/dto';
import { VM } from '../../../app/core/models/vm';
import { environment } from '../../../environments/environment';
import { MyInfo, PrizePoolList, RankingPlayerList } from '../../core/interface/leaderboard.interface';

interface GroupSubset {
    index: number;
    subsetId: number;
    promotionId: number;
    promotionNationalities: string;
    name: string;
}

interface GroupSet {
    index: number;
    setId: number;
    name: string;
    statedAt: string;
    finishedAt: string;
    isPeriodDate: boolean;
    subsets: GroupSubset[];
}

@Component({
    selector: 'app-promotion-leaderboard-group',
    templateUrl: 'promotion-leaderboard-group.component.html',
    styleUrls: ['promotion-leaderboard-group.component.scss']
})
export class PromotionLeaderboardGroupComponent implements OnInit {
    public progress = false;
    public isMaintenance: boolean;
    public isError: boolean;
    public activatedSet: GroupSet;
    public activatedSubSet: GroupSubset;
    public prevSet: GroupSet;
    public nextSet: GroupSet;
    public sets: GroupSet[];
    public type: DTO.Enums.PromotionType;
    public nickname: string;
    public brandId: string;
    public siteId: string;
    public prizeVisibility = false;
    public playerLimit = 250;
    public isLayerOpen = false;
    public selectedLeaderboardDate: Date;
    public todayLeaderboardDate: Date;
    public minDate: Date;
    public maxDate: Date;
    public invalidDates: Date[];
    public isPeriodDate = false;
    public timezone: VM.Enums.TimeZoneType;
    private now: Date = new Date();
    public utc: Date = new Date(this.now.getTime() + this.now.getTimezoneOffset() * 60000);
    public myInfo: MyInfo;
    public prizePoolList: PrizePoolList[];
    public rankingList: RankingPlayerList[];
    public isRefreshable = true;
    public countryCodes: { [key: string]: string } = {};
    public promotionId: number;
    public selectPromotion: any;
    public selectPromotionId: number;
    public isScrolling = false;
    private environment = environment.env;
    public onlyCoin = false;
    public hideSymbol = false;
    public hideCustomSymbol = false;
    public nationality = null;
    public hideRank = false;
    public focusFirstPlayer = false;
    public nationalityName = null;
    private countryFlagMap = new Map<string, any>([
        ['UA', { RU: 'RU_WB' }],
        ['CN', { TW: 'CN' }],
        ['LB', { IL: 'CY' }]
    ]);
    public countryCode: string;
    public disabeldNavigate;
    public navigateButton = {
        prev: true,
        next: true
    };

    private wsopPrizeCustomList = {
        PROD: [43692],
        TEST: [27406]
    };

    constructor(
        private route: ActivatedRoute,
        private leaderboardService: PromotionLeaderboardService,
        private commonService: CommonService,
        private zone: NgZone,
        private cd: ChangeDetectorRef,
        private translateService: TranslateService) {
        this.translateService.setDefaultLang('en');
        this.selectedLeaderboardDate = new Date();
        this.timezone = VM.Enums.TimeZoneType.UTC_0;
    }

    public get isWsopPrizeCustom() {
        const env = this.environment !== 'PROD' ? 'TEST' : this.environment;
        return this.wsopPrizeCustomList[env].indexOf(Number(this.promotionId)) > -1;
    }

    public get isChase() {
        return this.type === DTO.Enums.PromotionType.CHASE;
    }

    public get isTournamentLeaderBoard() {
        return this.type === DTO.Enums.PromotionType.TOURNAMENT_LEADERBOARD;
    }

    public async ngOnInit() {
        let lang = this.route.snapshot.queryParams['lang'];
        lang = (Array.isArray(lang) ? lang.pop() : lang) || 'en';
        if (lang === 'zh') {
            lang = 'zh-cn';
        }
        this.translateService.use(lang);

        if (!!this.route.snapshot.queryParams['nickname']) {
            this.nickname = this.route.snapshot.queryParams['nickname'];
        }

        if (!!this.route.snapshot.queryParams['brandId']) {
            this.brandId = this.route.snapshot.queryParams['brandId'];
        }

        if (!!this.route.snapshot.queryParams['siteId']) {
            this.siteId = this.route.snapshot.queryParams['siteId'];
        }

        if (!!this.route.snapshot.queryParams['timezone']) {
            this.timezone = this.route.snapshot.queryParams['timezone'];
            this.initUTCDate();
        }

        this.onlyCoin = !!this.route.snapshot.queryParams['onlyCoin'] ? 'on' === this.route.snapshot.queryParams['onlyCoin'] : false;

        // prize=off 의 경우 prizeVisibility = false
        this.prizeVisibility = !this.route.snapshot.queryParams['prize'] || 'off' !== this.route.snapshot.queryParams['prize'];

        this.hideSymbol = !!this.route.snapshot.queryParams['hs'] ? 'on' === this.route.snapshot.queryParams['hs'] : false;
        this.hideCustomSymbol = !!this.route.snapshot.queryParams['hcs'] ? 'on' === this.route.snapshot.queryParams['hcs'] : false;
        this.hideRank = !!this.route.snapshot.queryParams['hr'] ? 'on' === this.route.snapshot.queryParams['hr'] : false;

        if (!!this.route.snapshot.queryParams['nationality']) {
            this.nationality = this.route.snapshot.queryParams['nationality'];
        }

        if (!!this.route.snapshot.queryParams['promotion']) {
            this.selectPromotionId = Number(this.route.snapshot.queryParams['promotion']);
        }

        this.focusFirstPlayer = this.route.snapshot.queryParams['fp'] ? 'on' === this.route.snapshot.queryParams['fp'].toLowerCase() : false;
        await this.loadGroup();
    }

    private async checkMaintenance() {
        try {
            const maintenance = await this.commonService.getMaintenance();
            this.isMaintenance = maintenance !== 'OK';
        } catch (e) {
            this.isMaintenance = true;
            console.error(e);
        }
    }

    private async loadGroup() {
        this.progress = true;
        this.isError = false;
        await this.checkMaintenance();
        if (this.isMaintenance) {
            this.progress = false;
            return;
        }

        const groupId = this.route.snapshot.queryParams['groupId'];
        if (!groupId) {
            this.isError = true;
            this.progress = false;
            return;
        }

        try {
            const group = await this.leaderboardService.getPromotionGroup(groupId);
            this.type = group.type;

            if (!group.sets || group.sets.length < 1) {
                this.isError = true;
                this.progress = false;
                return;
            }
            const dateFormat = 'DD/MM/YYYY';

            this.sets = (group.sets || [])
            .sort((a, b) => new Date(moment(a.name, dateFormat).format()).getTime() - new Date(moment(b.name, dateFormat).format()).getTime())
            .map((set, index) => {
                const isPeriodDate = set.name.indexOf('~') > -1;
                return {
                    index,
                    setId: set.setId,
                    name: set.name,
                    statedAt: set.startedAt,
                    finishedAt: set.finishedAt,
                    subsets: set.subsets.sort((a, b) => a.subsetId - b.subsetId).map((subset, subIndex) => {
                        const subsetExplode = subset.name.trim().split('|');
                        let itemName;
                        if (this.onlyCoin) {
                            itemName = subsetExplode.length > 1 ? subsetExplode[1] : subsetExplode[0];
                        } else {
                            itemName = subsetExplode[0];
                        }
                        return {
                            index: subIndex,
                            subsetId: subset.subsetId,
                            promotionId: subset.promotionId,
                            name: itemName,
                            promotionNationalities: subset.promotionNationalities.find(sets => sets.operator === 'CONTAINS_NATIONALITY_COUNTRY') ? subset.promotionNationalities.find(sets => sets.operator === 'CONTAINS_NATIONALITY_COUNTRY').value : null
                        };
                    }),
                    isPeriodDate
                };
            });
            if (this.onlyCoin) {
                this.sets.map(item => {
                    item.subsets = item.subsets.filter(subsets => subsets.name !== 'noneItem');
                });
            }

            this.isPeriodDate = this.sets.every(set => set.isPeriodDate === true);
            if (!this.isPeriodDate) {
                this.setDateNavigate();
                this.setDateForWeeklyPromotion();
            } else {
                const now = moment.utc();
                this.navigateSet((this.sets.find(set => {
                    return moment.utc(set.statedAt) <= now && moment.utc(set.finishedAt) >= now;
                }) || this.sets[0]).setId);
            }
        } catch (e) {
            this.isError = true;
            console.error(e);
        } finally {
            this.progress = false;
        }
    }

    private initUTCDate() {
        let hour = 0;
        switch (this.timezone) {
            case VM.Enums.TimeZoneType.UTC_0:
                hour = 0;
                break;
            case VM.Enums.TimeZoneType.UTC_7:
                hour = -7;
                break;
            case VM.Enums.TimeZoneType.UTC_8:
                hour = -8;
                break;
        }

        const utcDate = moment.utc(this.selectedLeaderboardDate).add(moment().utcOffset() * -1, 'minutes').add(hour, 'hours').format();
        this.selectedLeaderboardDate = new Date(utcDate);
        this.todayLeaderboardDate = new Date(utcDate);
    }

    public async navigateSet(setId: number) {
        this.activatedSet = this.sets.find(set => set.setId === setId);
        if (this.selectPromotionId) {
            if (this.selectPromotion && this.activatedSet.subsets.find(item => item.name === this.selectPromotion)) {
                this.activatedSubSet = this.activatedSet.subsets.find(item => item.name === this.selectPromotion);
            } else {
                this.activatedSubSet = this.activatedSet.subsets.find(item => item.promotionId === this.selectPromotionId) ? this.activatedSet.subsets.find(item => item.promotionId === this.selectPromotionId) : this.activatedSet.subsets[0];
            }
        } else {
            if (this.selectPromotion && this.activatedSet.subsets.find(item => item.name === this.selectPromotion)) {
                this.activatedSubSet = this.activatedSet.subsets.find(item => item.name === this.selectPromotion);
            } else {
                this.activatedSubSet = this.activatedSet.subsets[0];
            }
        }

        const index = this.sets.indexOf(this.activatedSet);
        this.prevSet = this.sets[index - 1] || null;
        this.nextSet = this.sets[index + 1] || null;

        this.isLayerOpen = false;

        this.promotionId = this.activatedSubSet.promotionId;
        this.selectPromotion = this.activatedSubSet.name;
        await this.load();
    }

    public async setDateNavigate() {
        const dateFormat = 'DD/MM/YYYY';
        const selectedDate = moment(this.selectedLeaderboardDate).format(dateFormat);

        this.activatedSet = this.sets.some(set => set.name === selectedDate) ? this.sets.find(set => set.name === selectedDate) : this.sets[0];
        this.selectedLeaderboardDate = new Date(moment(this.activatedSet.name, dateFormat).format());
        // console.log('%c' + this.selectedLeaderboardDate, 'background: #222; color: yellow');

        this.minDate = new Date(moment(this.sets[0].name, dateFormat).format());
        this.maxDate = new Date(moment(this.sets[this.sets.length - 1].name, dateFormat).format());

        this.disabeldNavigate = {
            selectedMonth: moment(this.selectedLeaderboardDate, dateFormat).format('YYYY-MM'),
            minMonth: moment(this.minDate, dateFormat).format('YYYY-MM'),
            maxMonth: moment(this.maxDate, dateFormat).format('YYYY-MM'),
        };

        const fillDates = [];
        const diffDay = moment(this.maxDate).diff(moment(this.minDate), 'day');

        for (let i = 0; i <= diffDay; i++) {
            fillDates.push(moment(this.minDate, dateFormat).add(i, 'day').format(dateFormat));
        }

        this.invalidDates = fillDates.filter(day => this.sets.every(set => set.name !== day)).map(d => new Date(moment(d, dateFormat).format('YYYY-MM-DD')));
        if (this.selectPromotionId) {
            if (this.selectPromotion && this.activatedSet.subsets.find(item => item.name === this.selectPromotion)) {
                this.activatedSubSet = this.activatedSet.subsets.find(item => item.name === this.selectPromotion);
            } else {
                if (this.activatedSet.subsets.find(item => item.promotionId === this.selectPromotionId)) {
                    this.activatedSubSet = this.activatedSet.subsets.find(item => item.promotionId === this.selectPromotionId);
                } else {
                    this.activatedSubSet = (this.selectPromotion && this.activatedSet.subsets.find(item => item.name === this.selectPromotion)) ? this.activatedSet.subsets.find(item => item.name === this.selectPromotion) : this.activatedSet.subsets[0];
                }
            }
        } else {
            this.activatedSubSet = (this.selectPromotion && this.activatedSet.subsets.find(item => item.name === this.selectPromotion)) ? this.activatedSet.subsets.find(item => item.name === this.selectPromotion) : this.activatedSet.subsets[0];
            if (this.nationality && this.activatedSet.subsets.find(item => item.promotionNationalities && item.promotionNationalities.includes(this.nationality))) {
                this.activatedSubSet = this.activatedSet.subsets.find(item => item.promotionNationalities && item.promotionNationalities.includes(this.nationality));
                this.nationalityName = this.activatedSubSet.name;
            }
        }

        this.selectPromotion = this.activatedSubSet.name;
        this.promotionId = this.activatedSubSet.promotionId;
        this.isLayerOpen = false;

        if (this.promotionId) {
            await this.load();
        }
        this.checkMonth();
    }

    public checkMonth(event?: { year: number; month: string | number }) {
        let selectedMonth;
        if (event) {
            event.month = event.month < 10 ? `0${event.month}` : event.month;
            selectedMonth = `${event.year}-${event.month}`;
        } else {
            selectedMonth = this.disabeldNavigate.selectedMonth;
        }
        const prev = selectedMonth === this.disabeldNavigate.minMonth;
        const next = selectedMonth === this.disabeldNavigate.maxMonth;
        this.navigateButton = {
            prev: prev,
            next: next
        };
    }

    private setDateForWeeklyPromotion() {
        const foundWeek = this.sets.find(set => 0 < moment(set.finishedAt).diff(moment(set.statedAt), 'days') && moment().isBetween(set.statedAt, set.finishedAt));
        if (!!foundWeek) {
            this.activatedSet = foundWeek;
            this.selectedLeaderboardDate = new Date(moment(foundWeek.statedAt).format());
            this.activatedSubSet = foundWeek.subsets[0];
            if (this.nationality && this.activatedSet.subsets.find(item => item.promotionNationalities && item.promotionNationalities.includes(this.nationality))) {
                this.activatedSubSet = this.activatedSet.subsets.find(item => item.promotionNationalities && item.promotionNationalities.includes(this.nationality));
                this.nationalityName = this.activatedSubSet.name;
            }
            this.promotionId = this.activatedSubSet.promotionId;
        }
    }

    public async toggleSelectLayer() {
        this.isLayerOpen = !this.isLayerOpen;
    }

    public async changeActivatedSubSet(subset) {
        this.activatedSubSet = subset;
        this.promotionId = subset.promotionId;
        this.selectPromotion = this.activatedSubSet.name;
        await this.load();
    }

    private async load() {
        this.progress = true;

        const playersLimit = await this.getNumberOfPlayers();
        let promises = [];
        if (!this.hideRank) {
            promises = [
                this.getCountryCodes(),
                this.getMyInfo(),
                this.getPlayerRankingList(playersLimit)
            ];
        } else {
            promises = [
                this.getCountryCodes(),
                this.getPlayerRankingList(playersLimit)
            ];
        }

        if (this.isChase) {
            promises.push(this.getPrizePoolList());
        }

        await Promise.all(promises);
        this.countryCode = this.leaderboardService.countryCode;
        this.progress = false;
    }

    private async getCountryCodes() {
        try {
            this.countryCodes = await this.commonService.getCountryCodes();
        } catch (e) {
            console.error(e);
        }
    }

    private async getMyInfo() {
        try {
            if (!this.nickname) {
                this.myInfo = null;
                return;
            }
            const targetParams = {
                promotionId: this.promotionId,
                nickname: this.nickname,
                brandId: this.brandId,
                siteId: this.siteId
            };

            this.myInfo = await this.leaderboardService.getMyInfo(targetParams, !this.prizeVisibility);
        } catch (e) {
            this.myInfo = null;
            console.error(e);
        }
    }

    private async getPlayerRankingList(limit: number) {
        try {
            const ret = await this.leaderboardService.getRankingPlayerList({
                promotionId: this.promotionId,
                nickname: this.nickname
            }, limit, !this.prizeVisibility);
            this.rankingList = (this.isTournamentLeaderBoard ? ret : ret.filter(row => row.isCorrect)).map(row => ({
                ...row,
                displayCountryId: this.getDisplayCountryCode(row.countryId)
            }));

            const isMobile = navigator.userAgent.match(/Mobile|iP(hone|od)|BlackBerry|IEMobile|Kindle|NetFront|Silk-Accelerated|(hpw|web)OS|Fennec|Minimo|Opera M(obi|ini)|Blazer|Dolfin|Dolphin|Skyfire|Zune/);
            this.isScrolling = this.rankingList.length > 10 && !isMobile;
        } catch (e) {
            this.rankingList = [];
        }
    }

    private getDisplayCountryCode(countryId: string) {
        if (!this.countryFlagMap.has(this.leaderboardService.countryCode)) {
            return countryId;
        }

        const flagMap = this.countryFlagMap.get(this.leaderboardService.countryCode);
        return Object.prototype.hasOwnProperty.call(flagMap, countryId) ? flagMap[countryId] : countryId;
    }

    private async getPrizePoolList(limit: number = null) {
        try {
            const prizePoolList = await this.leaderboardService.getPrizePoolList({ promotionId: this.promotionId });
            const selectedPrizePool = prizePoolList.find(prizePool => prizePool.winners !== prizePool.maxWinners) || prizePoolList[prizePoolList.length - 1];
            if (!!selectedPrizePool) {
                selectedPrizePool.isRunning = true;
            }
            this.prizePoolList = prizePoolList;

            const selectedLevel = selectedPrizePool.level;
            const rankingLimit = (
                (
                    selectedLevel === 1 ?
                        selectedPrizePool
                        : prizePoolList.find(prizePool => prizePool.level === selectedPrizePool.level - 1)
                ) || {} as PrizePoolList
            ).maxWinners || 0;

            await this.getPlayerRankingList(limit ? limit : Math.max(rankingLimit, 10));
        } catch (e) {
            this.prizePoolList = [];
            console.error(e);
        }
    }

    private async getNumberOfPlayers(): Promise<number> {
        try {
            const promotionDetail = await this.leaderboardService.getPromotionType(this.promotionId);
            if (!promotionDetail['description']) {
                return 250;
            }
            const description = JSON.parse(promotionDetail['description']);
            return !!description.displayedPlayers ? description.displayedPlayers : 250;
        } catch (e) {
            console.error(e);
            return 250;
        }
    }

    public async refresh(event: Event) {
        event.preventDefault();

        let requestTimer = null;
        const DELAY_SECOND = 5000;

        requestTimer = setTimeout(() => {
            this.isRefreshable = true;
            clearTimeout(requestTimer);
        }, DELAY_SECOND);

        await this.load();
        this.isRefreshable = false;
    }
}
